import React from 'react';
import { DevelopmentScenario } from '@/services/fsiReport';
import { Settings, Building, Home as HomeIcon, Layers } from 'lucide-react';

interface CustomOptionsFormProps {
  scenario: DevelopmentScenario | undefined;
  fsiUtilization: string;
  zoneFsiConfig: {
    base_fsi: number;
    max_paid_fsi: number;
    total_max_fsi: number;
    tdr_allowed: boolean;
    tdr_max_additional: number;
  };
  customOptions: {
    floor_count?: number;
    basement_count?: number;
    has_tdr?: boolean;
    tdr_area_sqm?: number;
  };
  onChange: (value: any) => void;
}

const CustomOptionsForm: React.FC<CustomOptionsFormProps> = ({
  scenario,
  fsiUtilization,
  zoneFsiConfig,
  customOptions,
  onChange,
}) => {
  if (!scenario) return null;

  const showTDROption = fsiUtilization === 'with_tdr' && zoneFsiConfig.tdr_allowed;
  const maxFloors = scenario.max_floors || 50;

  return (
    <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-6 mb-6 border border-gray-200">
      <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
        <Settings className="w-5 h-5 text-green-600" />
        Customize Your Project (Optional)
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Floor Count */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <Building className="w-4 h-4 text-blue-600" />
            Number of Floors
          </label>
          <input
            type="number"
            min="1"
            max={maxFloors}
            value={customOptions.floor_count || scenario.max_floors || 10}
            onChange={(e) => onChange({ floor_count: parseInt(e.target.value) || undefined })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            {scenario.max_floors ? `Maximum: ${scenario.max_floors} floors` : 'No maximum limit specified'}
          </p>
        </div>

        {/* Basement Count */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <Layers className="w-4 h-4 text-orange-600" />
            Basement Floors
          </label>
          <select
            value={customOptions.basement_count || 0}
            onChange={(e) => onChange({ basement_count: parseInt(e.target.value) })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value={0}>No Basement</option>
            <option value={1}>1 Basement Level</option>
            <option value={2}>2 Basement Levels</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Basement area not included in FSI calculation
          </p>
        </div>

        {/* TDR Options - Only show if with_tdr is selected */}
        {showTDROption && (
          <>
            <div className="md:col-span-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={customOptions.has_tdr || false}
                  onChange={(e) => onChange({ has_tdr: e.target.checked, tdr_area_sqm: e.target.checked ? customOptions.tdr_area_sqm : 0 })}
                  className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Utilize Transfer of Development Rights (TDR)
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-8">
                Maximum additional FSI from TDR: {zoneFsiConfig.tdr_max_additional}
              </p>
            </div>

            {customOptions.has_tdr && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  TDR Area (sq m)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={customOptions.tdr_area_sqm || 0}
                  onChange={(e) => onChange({ tdr_area_sqm: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Enter TDR area in square meters"
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Info Box */}
      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> These settings allow you to fine-tune your development plan. 
          Leave default values if you're unsure, or adjust based on your specific requirements.
        </p>
      </div>
    </div>
  );
};

export default CustomOptionsForm;
